import React from "react";
import PageHeader from "components/odl-v2/PageHeader/PageHeader";
import { useTranslation } from "react-i18next";
import { useStoreSelector } from "store/hooks";
import { selectUserInfoEntityForAuthenticatedUser } from "store/domain-data/user-info/userInfo";
import { css } from "@emotion/react";
import { styled } from "@mui/material/styles";
import ActionSection from "components/ActionSection/ActionSection";
import ActionSectionAlt from "components/ActionSectionAlt/ActionSectionAlt";
import ThreeColumnsLayout from "components/odl-v2/PageLayout/ThreeColumnsLayout";
import PrimarySideNavigation from "components/PrimarySideNavigation/PrimarySideNavigation";
import { PrimarySideNavigationItemType } from "components/PrimarySideNavigation/PrimarySideNavigation.types";
import ApplicationsSection from "pages/dashboard/ApplicationsSection";
import { useApplicationIntentActionItems } from "hooks/useApplicationIntentActionItems";
import StartApplicationSection from "pages/dashboard/StartApplicationSection";
import AlertsSection from "pages/dashboard/AlertsSection";
import { selectIsSideNavigationCollapsed } from "store/app-state/ui/ui";
import { useScreenWidthMatch } from "hooks/useScreenWidthMatch";
import { FOCUS_DASHBOARD_ENABLED } from "constants/configs";
import { IAccessRequestParams } from "models/AccessRequest.model";
import OutstandingActionsSection from "./OutstandingActionsSection";
import { useAccessRequestActions } from "hooks/useAccessRequestActions";
import { useToast } from "hooks/useToast";
import { serviceContainer } from "services/serviceContainer";
import { useHistory } from "react-router-dom";

type Props = {
  initialAccessRequest?: IAccessRequestParams;
};

const DashboardPage: React.FC<Props> = ({ initialAccessRequest }) => {
  const { t } = useTranslation();
  const { toastError } = useToast();
  const history = useHistory();
  const currentUser = useStoreSelector(selectUserInfoEntityForAuthenticatedUser);

  const screenWidthMatch = useScreenWidthMatch();
  const { requestAccess } = useAccessRequestActions();
  const {
    actionItems: applicationIntentActionItems,
    accessRequestDialog,
    accessRequestConfirmationDialog,
    openAccessRequestConfirmationDialog,
  } = useApplicationIntentActionItems();

  const isSideNavigationCollapsed = useStoreSelector(selectIsSideNavigationCollapsed);

  const pageTitle = React.useMemo(() => {
    if (screenWidthMatch.sm) {
      return t(`Welcome to your dashboard {{firstName}}`, { firstName: currentUser?.firstName });
    }

    return t(`Welcome {{firstName}}`, { firstName: currentUser?.firstName });
  }, [currentUser?.firstName, screenWidthMatch.sm, t]);

  React.useEffect(() => {
    const requestApplicationAccess = async (applicationId: number) => {
      const logger = serviceContainer.cradle.logger;
      try {
        await requestAccess(applicationId);
        openAccessRequestConfirmationDialog();
      } catch (err) {
        logger.error("Participant access request submit failed", err);
        toastError(err);
      }
    };

    if (initialAccessRequest) {
      // Automatically request access if initialAccessRequest is supplied
      history.replace("/");
      requestApplicationAccess(initialAccessRequest.applicationId);
    }
  }, [history, initialAccessRequest, openAccessRequestConfirmationDialog, requestAccess, toastError]);

  if (!currentUser) {
    return null;
  }

  return (
    <ThreeColumnsLayout
      data-testid="DashboardPage"
      pageHeader={<PageHeader isBackButtonVisible={false} title={pageTitle} />}
      isLeftColumnCollapsed={isSideNavigationCollapsed}
      leftColumn={<PrimarySideNavigation activeNavigationItemType={PrimarySideNavigationItemType.Dashboard} />}
      rightColumn={<ActionSection items={screenWidthMatch.md ? applicationIntentActionItems : []} />}
      rightColumnAlt={<ActionSectionAlt items={screenWidthMatch.md ? applicationIntentActionItems : []} />}
    >
      <StyledContainer>
        <AlertsSection />
        {screenWidthMatch.md && <StartApplicationSection />}
        {!FOCUS_DASHBOARD_ENABLED && <ApplicationsSection />}
        {FOCUS_DASHBOARD_ENABLED && <OutstandingActionsSection />}
      </StyledContainer>
      {accessRequestDialog}
      {accessRequestConfirmationDialog}
    </ThreeColumnsLayout>
  );
};

const StyledContainer = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding-bottom: 32px;
    ${theme.mixins.flexGap("24px")}
  `
);

export default DashboardPage;
